import { marketData } from '@/services/alpaca';
import { MarketIndex } from '@/services/market-data-service';

// Mapping of ETF symbols to their display names
const INDEX_NAMES: Record<string, string> = {
  'SPY': 'S&P 500',
  'QQQ': 'Nasdaq 100',
  'DIA': 'Dow Jones',
  'IEF': '7-10 Yr Treasury'
};

// The symbols we want to track (SPY, QQQ, DIA, IEF)
export const INDEX_SYMBOLS = Object.keys(INDEX_NAMES);

// Cache previous day's closing prices
let previousClosePrices: Record<string, number> = {};
let previousCloseLastFetched: number = 0;
const PREVIOUS_CLOSE_CACHE_DURATION = 12 * 60 * 60 * 1000; // 12 hours

// Market hours cache
let marketHoursCache: { isOpen: boolean; nextOpenTime: Date | null; nextCloseTime: Date | null } | null = null;
let marketHoursLastFetched: number = 0;
const MARKET_HOURS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Check if the market is currently open
 * @returns Object with isOpen status and next open/close times
 */
export async function isMarketOpen(): Promise<{ 
  isOpen: boolean; 
  nextOpenTime: Date | null; 
  nextCloseTime: Date | null;
}> {
  // Check if we have cached data that's still fresh
  const now = Date.now();
  if (marketHoursCache && 
      now - marketHoursLastFetched < MARKET_HOURS_CACHE_DURATION) {
    return marketHoursCache;
  }
  
  try {
    // Get current time in UTC
    const currentDate = new Date();
    
    // Determine if we're in Daylight Saving Time (EST vs EDT)
    const isDST = isDaylightSavingsTime(currentDate);
    
    // EST is UTC-5, EDT is UTC-4
    const estOffset = isDST ? -4 : -5;
    
    // Convert UTC to Eastern Time
    const estTime = new Date(currentDate.getTime() + (estOffset * 60 * 60 * 1000));
    const estDay = estTime.getUTCDay(); // 0 = Sunday, 1 = Monday, etc.
    const estHour = estTime.getUTCHours();
    const estMinute = estTime.getUTCMinutes();
    
    // Check if it's a weekday (Monday=1 to Friday=5) and within market hours (9:30 AM - 4:00 PM ET)
    const isWeekday = estDay >= 1 && estDay <= 5;
    const isWithinHours = (estHour > 9 || (estHour === 9 && estMinute >= 30)) && estHour < 16;
    const isOpen = isWeekday && isWithinHours;
    
    // Calculate next open/close times in EST
    let nextOpenTime: Date | null = null;
    let nextCloseTime: Date | null = null;
    
    if (isOpen) {
      // Market is open, next close is today at 4:00 PM ET
      nextCloseTime = new Date(currentDate);
      nextCloseTime.setUTCHours(16 - estOffset, 0, 0, 0); // 4 PM ET in UTC
    } else {
      // Market is closed, calculate next open time
      nextOpenTime = getNextMarketOpenTime(currentDate, estOffset);
    }
    
    // Update cache
    marketHoursCache = { isOpen, nextOpenTime, nextCloseTime };
    marketHoursLastFetched = now;
    
    return marketHoursCache;
  } catch (error) {
    console.error('Error checking market hours:', error);
    // Default to closed if there's an error
    return { isOpen: false, nextOpenTime: null, nextCloseTime: null };
  }
}

/**
 * Helper function to calculate the next market open time
 */
function getNextMarketOpenTime(now: Date, estOffset: number): Date {
  // Convert current UTC time to Eastern Time to determine the day and time
  const estTime = new Date(now.getTime() + (estOffset * 60 * 60 * 1000));
  const estDay = estTime.getUTCDay(); // 0 = Sunday, 1 = Monday, etc.
  const estHour = estTime.getUTCHours();
  const estMinute = estTime.getUTCMinutes();
  
  const result = new Date(now);
  
  // Set time to 9:30 AM ET in UTC
  result.setUTCHours(9 - estOffset, 30, 0, 0); // 9:30 AM ET converted to UTC
  
  // If it's before 9:30 AM ET on a weekday, open is today
  if (estDay >= 1 && estDay <= 5 && (estHour < 9 || (estHour === 9 && estMinute < 30))) {
    return result;
  }
  
  // If it's after 4 PM ET on Friday or weekend, next open is Monday
  if (estDay === 5 && estHour >= 16) {
    result.setDate(result.getDate() + 3); // Next Monday
  } else if (estDay === 6) {
    result.setDate(result.getDate() + 2); // Next Monday
  } else if (estDay === 0) {
    result.setDate(result.getDate() + 1); // Next Monday
  } else {
    // Weekday after close, next open is tomorrow
    result.setDate(result.getDate() + 1);
  }
  
  return result;
}

/**
 * Helper function to check if date is in Daylight Savings Time
 * This is a simple implementation and may not handle all edge cases
 */
function isDaylightSavingsTime(date: Date): boolean {
  // DST in the US starts on the second Sunday in March
  // and ends on the first Sunday in November
  const year = date.getFullYear();
  const march = new Date(year, 2, 1);
  const november = new Date(year, 10, 1);
  
  // Find second Sunday in March
  const daysUntilSecondSundayMarch = (14 - march.getDay()) % 7;
  const secondSundayMarch = new Date(year, 2, 1 + daysUntilSecondSundayMarch + 7);
  secondSundayMarch.setUTCHours(2, 0, 0, 0);
  
  // Find first Sunday in November
  const daysUntilFirstSundayNov = (7 - november.getDay()) % 7;
  const firstSundayNovember = new Date(year, 10, 1 + daysUntilFirstSundayNov);
  firstSundayNovember.setUTCHours(2, 0, 0, 0);
  
  return date >= secondSundayMarch && date < firstSundayNovember;
}

/**
 * Fetch previous day's closing prices for the indices
 */
async function fetchPreviousClosePrices(): Promise<Record<string, number>> {
  // Check if we have cached data that's still fresh
  const now = Date.now();
  if (previousCloseLastFetched > 0 && 
      now - previousCloseLastFetched < PREVIOUS_CLOSE_CACHE_DURATION &&
      Object.keys(previousClosePrices).length === INDEX_SYMBOLS.length) {
    return previousClosePrices;
  }
  
  try {
    const result: Record<string, number> = {};
    
    // Get yesterday's bars for all symbols
    // Use Alpaca marketData API to fetch historical bars
    const barsData = await marketData.getHistoricalBars({
      symbols: INDEX_SYMBOLS,
      timeframe: '1Day',
      limit: 2 // Get the last 2 days to ensure we have yesterday's close
    });
    
    // Extract the previous close from the data
    for (const symbol of INDEX_SYMBOLS) {
      if (barsData[symbol] && barsData[symbol].length > 0) {
        // The first bar should be yesterday's data
        const previousBar = barsData[symbol][0];
        result[symbol] = previousBar.close;
      }
    }
    
    // Update the cache
    previousClosePrices = result;
    previousCloseLastFetched = now;
    
    return result;
  } catch (error) {
    console.error('Error fetching previous close prices:', error);
    return {};
  }
}

/**
 * Fetch real-time market indices data using Alpaca API
 * @returns Promise<MarketIndex[]> Array of market indices with real-time data
 */
export async function getAlpacaMarketIndices(): Promise<MarketIndex[]> {
  try {
    console.log('Fetching market indices from Alpaca');
    
    // Fetch quotes and previous close prices in parallel
    const [quotesData, prevClosePrices, marketStatus] = await Promise.all([
      marketData.getRealTimeContext(INDEX_SYMBOLS),
      fetchPreviousClosePrices(),
      isMarketOpen()
    ]);
    
    // Map the quotes to MarketIndex objects
    const indices: MarketIndex[] = [];
    
    for (const symbol of INDEX_SYMBOLS) {
      const quoteData = quotesData[symbol];
      
      if (quoteData) {
        // Get the previous close price or estimate it if not available
        const prevClose = prevClosePrices[symbol] || (quoteData.price * 0.99); // Fallback to 1% less than current price
        
        // Calculate change and percent change
        const change = quoteData.price - prevClose;
        const changePercent = (change / prevClose) * 100;
        
        indices.push({
          name: INDEX_NAMES[symbol],
          symbol,
          value: quoteData.price,
          change,
          changePercent,
          lastUpdated: quoteData.timestamp,
          marketStatus: marketStatus.isOpen ? 'open' : 'closed'
        });
      }
    }
    
    if (indices.length === 0) {
      throw new Error('No valid market indices data returned from Alpaca');
    }
    
    return indices;
  } catch (error) {
    console.error('Error fetching market indices from Alpaca:', error);
    throw error;
  }
}

/**
 * Get market indices with fallback data if Alpaca API fails
 */
export async function getMarketIndicesWithFallback(): Promise<MarketIndex[]> {
  try {
    return await getAlpacaMarketIndices();
  } catch (error) {
    console.warn('Falling back to simulated market indices data:', error);
    
    // Provide fallback data similar to the original market-data-service
    return [
      { name: 'S&P 500', symbol: 'SPY', value: 500.73, change: 2.25, changePercent: 0.45, lastUpdated: new Date().toISOString(), marketStatus: 'closed' },
      { name: 'Nasdaq 100', symbol: 'QQQ', value: 425.88, change: 2.82, changePercent: 0.67, lastUpdated: new Date().toISOString(), marketStatus: 'closed' },
      { name: 'Dow Jones', symbol: 'DIA', value: 382.39, change: 0.89, changePercent: 0.23, lastUpdated: new Date().toISOString(), marketStatus: 'closed' },
      { name: '7-10 Yr Treasury', symbol: 'IEF', value: 93.58, change: -0.21, changePercent: -0.22, lastUpdated: new Date().toISOString(), marketStatus: 'closed' }
    ];
  }
} 